import type React from 'react';
import { useRef, useState } from 'react';
import type { AutocompleteCloseReason, AutocompleteInputChangeReason } from '@mui/material/useAutocomplete';
import { useTranslation } from 'react-i18next';

import type { OptionKey } from './field.types';
import { DEFAULT_SEARCH_DELAY_MS, useSearchEmitter } from './useSearchEmitter';

const foldText = (text: string): string =>
  text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

export const matchesSearch = (label: string, search: string): boolean => foldText(label).includes(foldText(search.trim()));

export const withSelected = <T>(options: readonly T[], selected: readonly T[], getOptionKey: (option: T) => OptionKey): T[] => {
  const listed = new Set(options.map(getOptionKey));
  return [...selected.filter((option) => !listed.has(getOptionKey(option))), ...options];
};

export interface VisibleOptionsInput<T> {
  readonly options: readonly T[];
  readonly selected: readonly T[];
  readonly draft: string | null;
  readonly remote: boolean;
  readonly filterSelectedOptions: boolean;
  readonly getOptionKey: (option: T) => OptionKey;
  readonly getOptionLabel: (option: T) => string;
}

export const visibleOptions = <T>({ options, selected, draft, remote, filterSelectedOptions, getOptionKey, getOptionLabel }: VisibleOptionsInput<T>): T[] => {
  const pool = draft !== null && remote ? options.slice() : withSelected(options, selected, getOptionKey);
  const chosen = new Set(selected.map(getOptionKey));
  const offered = filterSelectedOptions ? pool.filter((option) => !chosen.has(getOptionKey(option))) : pool;
  if (draft === null || remote) return offered;
  return offered.filter((option) => matchesSearch(getOptionLabel(option), draft));
};

interface ListStatusInput {
  readonly open: boolean;
  readonly busy: boolean;
  readonly count: number;
  readonly loadingText: React.ReactNode;
  readonly noOptionsText: React.ReactNode;
  readonly resultsText: (count: number) => string;
}

const listStatus = ({ open, busy, count, loadingText, noOptionsText, resultsText }: ListStatusInput): React.ReactNode => {
  if (!open) return null;
  if (busy) return loadingText;
  if (count === 0) return noOptionsText;
  return resultsText(count);
};

export interface AutocompleteFieldConfig<T> {
  readonly options: readonly T[];
  readonly selected: readonly T[];
  readonly getOptionKey: (option: T) => OptionKey;
  readonly getOptionLabel: (option: T) => string;
  readonly filterSelectedOptions: boolean;
  readonly interactive: boolean;
  readonly loading: boolean;
  readonly loadingText: React.ReactNode | undefined;
  readonly noOptionsText: React.ReactNode | undefined;
  readonly onSearch: ((term: string) => void) | undefined;
  readonly searchDelayMs: number | undefined;
  readonly onOpen: (() => void) | undefined;
  readonly onClose: (() => void) | undefined;
}

export interface AutocompleteListProps<T> {
  readonly open: boolean;
  readonly onOpen: () => void;
  readonly onClose: (event: React.SyntheticEvent, reason: AutocompleteCloseReason) => void;
  readonly onInputChange: (event: React.SyntheticEvent, text: string, reason: AutocompleteInputChangeReason) => void;
  readonly options: T[];
  readonly filterOptions: () => T[];
  readonly isOptionEqualToValue: (option: T, value: T) => boolean;
  readonly getOptionKey: (option: T) => OptionKey;
  readonly getOptionLabel: (option: T) => string;
  readonly loading: boolean;
  readonly loadingText: React.ReactNode;
  readonly noOptionsText: React.ReactNode;
  readonly openText: string;
  readonly closeText: string;
  readonly disableClearable: true;
}

export interface TextBoxHandlers {
  readonly onBlur: () => void;
  readonly onKeyDown: (event: React.KeyboardEvent) => void;
}

export interface AutocompleteField<T> {
  readonly draft: string | null;
  readonly busy: boolean;
  readonly status: React.ReactNode;
  readonly listProps: AutocompleteListProps<T>;
  readonly textBoxHandlers: TextBoxHandlers;
  readonly restart: () => void;
  readonly finish: () => void;
}

export function useAutocompleteField<T>(config: AutocompleteFieldConfig<T>): AutocompleteField<T> {
  const { options, selected, getOptionKey, getOptionLabel, filterSelectedOptions, interactive, loading, onOpen, onClose } = config;
  const { t } = useTranslation();
  const [listShown, setListShown] = useState(false);
  const [draft, setDraft] = useState<string | null>(null);
  const announcedOpen = useRef(false);
  const search = useSearchEmitter(config.onSearch, config.searchDelayMs ?? DEFAULT_SEARCH_DELAY_MS);

  const open = listShown && interactive;
  const busy = loading || search.pending;
  const visible = open ? visibleOptions({ options, selected, draft, remote: search.remote, filterSelectedOptions, getOptionKey, getOptionLabel }) : [];
  const loadingText = config.loadingText ?? t('coreComponents.fields.loading', 'Loading…');
  const noOptionsText = config.noOptionsText ?? t('coreComponents.fields.noOptions', 'No options');
  const resultsText = (count: number): string =>
    count === 1 ? t('coreComponents.fields.resultsOne', '1 result') : t('coreComponents.fields.results', '{{count}} results', { count });

  const showList = () => {
    setListShown(true);
    if (announcedOpen.current) return;
    announcedOpen.current = true;
    onOpen?.();
  };

  const hideList = () => {
    setListShown(false);
    if (!announcedOpen.current) return;
    announcedOpen.current = false;
    onClose?.();
  };

  const restart = () => {
    setDraft(null);
    search.reset();
  };

  const finish = () => {
    restart();
    hideList();
  };

  return {
    draft,
    busy,
    status: listStatus({ open, busy, count: visible.length, loadingText, noOptionsText, resultsText }),
    listProps: {
      open,
      onOpen: () => {
        if (interactive) showList();
      },
      onClose: (_event, reason) => {
        if (reason === 'toggleInput') hideList();
        else finish();
      },
      onInputChange: (_event, text, reason) => {
        if (reason !== 'input') return;
        setDraft(text);
        showList();
        search.schedule(text);
      },
      options: withSelected(options, selected, getOptionKey),
      filterOptions: () => visible,
      isOptionEqualToValue: (option, value) => getOptionKey(option) === getOptionKey(value),
      getOptionKey,
      getOptionLabel,
      loading: busy,
      loadingText,
      noOptionsText,
      openText: t('coreComponents.fields.open', 'Open'),
      closeText: t('coreComponents.fields.close', 'Close'),
      disableClearable: true,
    },
    textBoxHandlers: {
      onBlur: finish,
      onKeyDown: (event) => {
        if (event.key === 'Escape' && !open && draft !== null) restart();
      },
    },
    restart,
    finish,
  };
}
